import React from 'react';
import { Box, Typography, Paper, Chip, Divider, alpha } from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { SIDEBAR_WIDTH } from '../constants';
import OfferRow from './OfferRow';

// Dictionnaire des statuts et de leurs couleurs pour la légende
const STATUS_LEGEND = [
  { key: 'PENDING', label: 'En attente', color: '#F59E0B', bg: '#FEF3C7' },
  { key: 'CANCELLED', label: 'Annulé', color: '#EF4444', bg: '#FEE2E2' },
  { key: 'APPROVED', label: 'Confirmé', color: '#10B981', bg: '#D1FAE5' },
  { key: 'COMPLETED', label: 'Terminé', color: '#6366F1', bg: '#E0E7FF' },
];

// Dictionnaire des types d'intervenants
const ROLE_LEGEND = [
  { label: 'Intervenant', color: '#60A5FA' }, // Bleu clair
  { label: 'Praticien', color: '#F472B6' },   // Rose clair
];

const TimelineGrid = ({
  scrollContainerRef,
  onScroll,
  joursTimeline,
  indexToday,
  aujourdhuiStr,
  largeurCellule,
  offres,
  reservations,
  isIntervenant,
  onReservationClick,
  onOpenDay,
}) => (
  <Paper
    sx={{
      borderRadius: '24px',
      overflow: 'hidden',
      border: '1px solid #E0E5F2',
      boxShadow: '0px 20px 50px rgba(112, 144, 176, 0.08)',
    }}
  >
    {/* BARRE DE LÉGENDE / EXPLICATION DES COULEURS (UX) */}
    <Box
      sx={{
        p: 2,
        px: 3,
        bgcolor: '#FFFFFF',
        borderBottom: '1px solid #E0E5F2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {/* Légende Rôles */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1B254B' }}>
          Rôles :
        </Typography>
        {ROLE_LEGEND.map((role) => (
          <Box key={role.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: role.color,
              }}
            />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4A5568' }}>
              {role.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

      {/* Légende Statuts des réservations */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1B254B' }}>
          Statuts :
        </Typography>
        {STATUS_LEGEND.map((item) => (
          <Chip
            key={item.key}
            label={item.label}
            size="small"
            sx={{
              bgcolor: item.bg,
              color: item.color,
              fontWeight: 700,
              fontSize: '0.75rem',
              border: `1px solid ${alpha(item.color, 0.4)}`,
              height: '24px',
            }}
          />
        ))}
      </Box>
    </Box>

    <Box
      ref={scrollContainerRef}
      onScroll={onScroll}
      sx={{
        overflowX: 'auto',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        bgcolor: 'white',
      }}
    >
      {/* INDICATEUR AUJOURD'HUI */}
      {indexToday !== -1 && (
        <Box
          sx={{
            position: 'absolute',
            left: SIDEBAR_WIDTH + indexToday * largeurCellule,
            top: 0,
            bottom: 0,
            width: '2px',
            bgcolor: '#4318FF',
            zIndex: 50,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* EN-TÊTE DES JOURS */}
      <Box sx={{ display: 'flex', minWidth: 'max-content' }}>
        <Box
          sx={{
            minWidth: SIDEBAR_WIDTH,
            position: 'sticky',
            left: 0,
            zIndex: 60,
            bgcolor: '#F8FAFD',
            borderBottom: '2px solid #E0E5F2',
            borderRight: '2px solid #E0E5F2',
            p: 2.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '0.75rem',
              color: '#A3AED0',
              letterSpacing: '1px',
            }}
          >
            Soins Individuels
          </Typography>
        </Box>
        {joursTimeline.map((jour) => (
          <Box
            key={jour.isoStr}
            sx={{
              minWidth: largeurCellule,
              py: 2.5,
              textAlign: 'center',
              bgcolor:
                jour.isoStr === aujourdhuiStr
                  ? alpha('#4318FF', 0.04)
                  : '#F8FAFD',
              borderBottom: '2px solid #E0E5F2',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 800,
                color: jour.isoStr === aujourdhuiStr ? '#4318FF' : '#707EAE',
                textTransform: 'uppercase',
              }}
            >
              {format(jour.dateObj, 'EEE dd', { locale: fr })}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* LIGNES DES OFFRES */}
      {offres.map((offre) => (
        <OfferRow
          key={offre.id}
          offre={offre}
          joursTimeline={joursTimeline}
          reservations={reservations}
          aujourdhuiStr={aujourdhuiStr}
          largeurCellule={largeurCellule}
          isIntervenant={isIntervenant}
          onReservationClick={onReservationClick}
          onOpenDay={onOpenDay}
        />
      ))}
    </Box>
  </Paper>
);

export default TimelineGrid;